// Layout.jsx

import React, { useEffect, useState } from 'react';
import { Col, Row, TabContent, TabPane } from 'reactstrap';
import { useMemberstack } from "@memberstack/react";
import Loading from '../components/Loading';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import NotaireSupportWidget from '../components/NotaireWidgetChat';
import ModalBienvenueNotaire from '../components/ModalBienvenueNotaire';

// ----------------------
// Imports des composants Catalyst pour la sidebar, le navbar, etc.
// ----------------------
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
} from '../components/catalyst/sidebar';
import { SidebarLayout } from '../components/catalyst/sidebar-layout';
import { Navbar } from '../components/catalyst/navbar';
import { Avatar } from '../components/catalyst/avatar';
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../components/catalyst/dropdown';

// ----------------------
// Imports des icônes Heroicons
// ----------------------
import {
    ArrowRightStartOnRectangleIcon,
    ChevronUpIcon,
    ShieldCheckIcon,
    FolderIcon,
    UserIcon,
    ChatBubbleLeftRightIcon
} from '@heroicons/react/16/solid';
import {
    GlobeAltIcon,
    HomeIcon,
    QuestionMarkCircleIcon,
    CalendarDateRangeIcon,
    CodeBracketIcon
} from '@heroicons/react/20/solid';

// ----------------------
// Imports des vues/pages de l'application
// ----------------------
import Dashboard from './Dashboard';
import Affaires from './Affaires';
import Profil from './Profil';
import FicheNotaire from './FicheNotaire';
import Support from './Support';
import DemandeContact from './DemandeContact';
import Calendrier from './newcalendar/Calendrier';
import RendezVous from './RendezVous';
import Messagerie from './Messagerie';
import WidgetRdv from './WidgetRdv';
// Import du logo pour l'avatar du sidebar
import icnLogo from '../img/icn-logo.svg';
import { EnvelopeIcon } from '@heroicons/react/24/solid';

function Layout() {
    // Récupère l'instance Memberstack pour gérer l'authentification
    const memberstack = useMemberstack();
    // Hook pour naviguer de manière programmatique
    const navigate = useNavigate();
    // Hook pour obtenir le chemin courant (pour onglet actif)
    const location = useLocation();

    // États pour stocker les données récupérées
    const [member, setMember] = useState(null);            // données Memberstack
    const [user, setUser] = useState(null);                // enregistrement Airtable "user"
    const [infoNotaire, setInfoNotaire] = useState(null);  // enregistrement Airtable "info-notaire"
    const [demandeContact, setDemandeContact] = useState(null); // enregistrements Airtable "contact-notaire"
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Détermine l'onglet actif selon le chemin URL
    const getTabFromPath = (path) => {
        if (path.includes('/app/dashboard')) return '1';
        if (path.includes('/app/affaires')) return '2';
        if (path.includes('/app/profil')) return '3';
        if (path.includes('/app/fiche')) return 'fiche';
        if (path.includes('/app/demande-contact')) return 'demandeContact';
        if (path.includes('/app/support')) return 'support';
        if (path.includes('/app/calendrier')) return 'calendrier';      //  calendrier
        if (path.includes('/app/rendez-vous')) return 'rendez-vous';      //  rdv
        if (path.includes('/app/messagerie')) return 'messagerie';      //  messagerie
        if (path.includes('/app/widget-rdv')) return 'widget-rdv';
        return '1';
    };
    const currentActiveTab = getTabFromPath(location.pathname);

    // ----------------------
    // 1. Récupération du membre courant depuis Memberstack
    // ----------------------
    const fetchDataMemberstack = async () => {
        await memberstack.getCurrentMember()
            .then(({ data }) => {
                setMember(data);   // stocke les données du membre
            })
            .catch(err => console.error(err));
    };
    useEffect(() => {
        fetchDataMemberstack(); // au montage du composant
    }, []);

    // ----------------------
    // Affichage de la modal de bienvenue à la première connexion
    // ----------------------
    useEffect(() => {
        if (member?.id && localStorage.getItem('clotere_show_welcome') === '1') {
            setShowWelcomeModal(true);
        }
    }, [member]);

    function handleCloseWelcomeModal() {
        setShowWelcomeModal(false);
        localStorage.removeItem('clotere_show_welcome');
    }

    // ----------------------
    // 2. Récupération des infos "user" depuis Airtable
    // ----------------------
    useEffect(() => {
        // si le membre est authentifié et a un airtable_id
        if (member && member.metaData.airtable_id) {
            const URL = `https://api.airtable.com/v0/appD48APNaGA4GN0B/user/${member.metaData.airtable_id}`;
            fetch(URL, {
                method: "GET",
                headers: {
                    // Clé Airtable hardcodée comme dans la version serveur
                    Authorization: "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
                    'content-type': 'application/x-www-form-urlencoded',
                    Accept: "application/json, text/plain, /"
                },
            })
                .then(res => res.json())
                .then(res => {
                    setUser(res.fields);    // stocke les champs "user"
                })
                .catch(err => console.error(err));
        }
        // si pas encore validé (pas d'airtable_id), redirige vers onboarding
        if (member && !member.metaData.airtable_id) {
            navigate("/app/onboard-notaire/2");
        }
    }, [member, navigate]);

    // ----------------------
    // 3. Récupération des infos "info-notaire" depuis Airtable
    // ----------------------
   useEffect(() => {
  if (user && user.email) {
    const emailForLayout = (user.role === 'secretaire' && user.email_notaire) ? user.email_notaire : user.email;

    const formula = `{user}="${emailForLayout}"`;

    const URL = `https://api.airtable.com/v0/appD48APNaGA4GN0B/info-notaire?filterByFormula=${encodeURIComponent(formula)}`;

    fetch(URL, {
      method: "GET",
      headers: {
        Authorization: `Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02`,
        Accept: "application/json",
      },
    })
      .then((res) => res.json())
      .then((res) => {
        //console.log("res info notaire", res);

        if (res.records && res.records.length > 0) {
          setInfoNotaire(res.records[0].fields);
        } else {
          console.log("❌ Aucun record trouvé pour cet email");
          setInfoNotaire(null);
        }
      })
      .catch((err) => console.error(err));
  }
}, [user]);

    // ----------------------
    // 4. Récupération des demandes de contact depuis Airtable
    // ----------------------
    useEffect(() => {
        if (user && user.email) {
            const emailForLayout = (user.role === 'secretaire' && user.email_notaire) ? user.email_notaire : user.email;
            const URL = `https://api.airtable.com/v0/appD48APNaGA4GN0B/contact-notaire?filterByFormula=SEARCH("${emailForLayout}",{notaire})`;
            fetch(URL, {
                method: "GET",
                headers: {
                    Authorization: "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
                    'content-type': 'application/x-www-form-urlencoded',
                    Accept: "application/json, text/plain, /"
                },
            })
                .then(res => res.json())
                .then(res => {
                    setDemandeContact(res.records); // stocke les enregistrements "contact-notaire"
                })
                .catch(err => console.error(err));
        }
    }, [user]);



    // ----------------------
    // Affichage du layout une fois toutes les données chargées
    // ----------------------
    if (member && user && infoNotaire && demandeContact) {
        return (
            <SidebarLayout
                sidebar={
                    <Sidebar>
                        {/* --- Header de la sidebar avec logo --- */}
                        <SidebarHeader>
                            <Dropdown>
                                <DropdownButton as={SidebarItem} className="mb-2.5">
                                    <Avatar src={icnLogo} alt="Clotere" />
                                    <SidebarLabel>Clotere</SidebarLabel>
                                </DropdownButton>
                            </Dropdown>
                        </SidebarHeader>

                        {/* --- Corps de la sidebar : les liens de navigation --- */}
                        <SidebarBody>
                            <SidebarSection>
                                <Link to="/app/dashboard">
                                    <SidebarItem current={currentActiveTab === '1'}>
                                        <HomeIcon />
                                        <SidebarLabel>Tableau de bord</SidebarLabel>
                                    </SidebarItem>
                                </Link>
                                <Link to="/app/rendez-vous">
                                    <SidebarItem current={currentActiveTab === 'rendez-vous'}>
                                        <CalendarDateRangeIcon />
                                        <SidebarLabel>Rendez-vous</SidebarLabel>
                                    </SidebarItem>
                                    </Link>

                                <Link to="/app/affaires">
                                    <SidebarItem current={currentActiveTab === '2'}>
                                        <FolderIcon />
                                        <SidebarLabel>Vos affaires</SidebarLabel>
                                    </SidebarItem>
                                </Link>

                                {user.role !== 'secretaire' && (
                                    <Link to="/app/messagerie">
                                        <SidebarItem current={currentActiveTab === 'messagerie'}>
                                            <ChatBubbleLeftRightIcon />
                                            <SidebarLabel>Messagerie</SidebarLabel>
                                        </SidebarItem>
                                    </Link>
                                )}
                                
                                
                                
                                <Link to="/app/demande-contact">
                                    <SidebarItem current={currentActiveTab === 'demandeContact'}>
                                        <EnvelopeIcon />
                                        <SidebarLabel>Demandes de contact</SidebarLabel>
                                    </SidebarItem>
                                </Link>
                                <Link to="/app/fiche">
                                    <SidebarItem current={currentActiveTab === 'fiche'}>
                                        <GlobeAltIcon />
                                        <SidebarLabel>Fiche comptable</SidebarLabel>
                                    </SidebarItem>
                                </Link>
                                {user.role !== 'secretaire' && (
                                    <Link to="/app/calendrier">
                                        <SidebarItem current={currentActiveTab === 'calendrier'}>
                                            <CalendarDateRangeIcon />
                                            <SidebarLabel>Calendrier</SidebarLabel>
                                        </SidebarItem>
                                    </Link>
                                )}

                            </SidebarSection>
                            <SidebarSpacer />
                            <SidebarSection className="bg-indigo-100 text-white rounded-lg">
                                <Link to="/app/widget-rdv">
                                    <SidebarItem current={currentActiveTab === 'widget-rdv'}>
                                        <CodeBracketIcon />
                                        <SidebarLabel className="text-indigo-800">Widget Rendez-vous</SidebarLabel>
                                    </SidebarItem>
                                </Link>
                            </SidebarSection>
                            <SidebarSection className="mt-1">
                                
                                <Link to="/app/profil">
                                    <SidebarItem current={currentActiveTab === 'profil'}>
                                        <UserIcon />
                                        <SidebarLabel>Profil</SidebarLabel>
                                    </SidebarItem>
                                </Link>
                            </SidebarSection>
                        </SidebarBody>

                        {/* --- Footer de la sidebar avec menu utilisateur --- */}
                        <SidebarFooter>
                            <Dropdown>
                                <DropdownButton as={SidebarItem}>
                                    <div className="flex items-center gap-3">
                                        <div>
                                            <div className="font-medium">{user.prenom}</div>
                                            <div className="text-xs">{user.email}</div>
                                        </div>
                                    </div>
                                    <ChevronUpIcon />
                                </DropdownButton>
                                <DropdownMenu>
                                    <DropdownItem href="/app/profil">
                                        <UserIcon />
                                        <DropdownLabel>Profil</DropdownLabel>
                                    </DropdownItem>
                                    <DropdownDivider />
                                    <DropdownItem href="https://notaire.clotere.fr/cgv" target="_blank">
                                        <ShieldCheckIcon />
                                        <DropdownLabel>CGV</DropdownLabel>
                                    </DropdownItem>
                                    <DropdownDivider />
                                    <DropdownItem onClick={() =>
                                        memberstack.logout().then(() => window.location.replace('/'))
                                    }>
                                        <ArrowRightStartOnRectangleIcon />
                                        <DropdownLabel>Se déconnecter</DropdownLabel>
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </SidebarFooter>
                    </Sidebar>
                }
                navbar={<Navbar />}
            >
                {/* --- Contenu principal : onglets et pages --- */}
                    <TabContent activeTab={currentActiveTab} key={location.pathname}>
                    {/* Tableau de bord */}
                    <TabPane tabId="1">
                        <Row>
                            <Col sm="12">
                                <Dashboard
                                    user={user}
                                    infoNotaire={infoNotaire}
                                    demandeContact={demandeContact}
                                />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Vos affaires */}
                    <TabPane tabId="2">
                        <Row>
                            <Col sm="12">
                                <Affaires user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Profil */}
                    <TabPane tabId="3">
                        <Row>
                            <Col sm="12">
                                <Profil user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Fiche notaire */}
                    <TabPane tabId="fiche">
                        <Row>
                            <Col sm="12">
                                <FicheNotaire user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Demande de contact  */}
                    <TabPane tabId="demandeContact">
                        <Row>
                            <Col sm="12">
                                <DemandeContact user={user} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Liste rdv */}
                    <TabPane tabId="rendez-vous">
                        <Row>
                            <Col sm="12">
                                <RendezVous user={user} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Ancien composant Calendrier */}
                    <TabPane tabId="calendrier">
                        <Row>
                            <Col sm="12">
                                <Calendrier />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Widget Rendez-vous */}
                    <TabPane tabId="widget-rdv">
                        <Row>
                            <Col sm="12">
                                <WidgetRdv user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Support */}
                    <TabPane tabId="support">
                        <Row>
                            <Col sm="12">
                                <Support user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>

                     {/* Messagerie  stand by pour intégration future*/}
                    <TabPane tabId="messagerie">
                        <Row>
                            <Col sm="12">
                                <Messagerie user={user} infoNotaire={infoNotaire} />
                            </Col>
                        </Row>
                    </TabPane>
                    
                </TabContent>

                <NotaireSupportWidget
    userEmail={user.email}
    userName={`${user.prenom || ""}`}
  />

                <ModalBienvenueNotaire
                    open={showWelcomeModal}
                    onClose={handleCloseWelcomeModal}
                />
            </SidebarLayout>
        );
    }

    // Affiche le composant Loading tant que les données ne sont pas prêtes
    return <Loading />;
}

export default Layout;
